import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

const faqs = [
  {
    q: 'How accurate is SysNerve\'s AI bug analysis?',
    a: 'SysNerve achieves over 98% accuracy on real-world bug reports. Our AI is trained on millions of bug reports, stack traces, and debugging sessions across dozens of languages and frameworks. Accuracy varies slightly by language and complexity, but critical bugs are almost never missed.',
  },
  {
    q: 'Which programming languages and frameworks does SysNerve support?',
    a: 'SysNerve supports all major programming languages including JavaScript, TypeScript, Python, Java, Go, Rust, C#, PHP, Ruby, and more. It also understands frameworks like Next.js, Django, Spring Boot, Laravel, Rails, Express, NestJS, and others. If you can write it, we can analyze it.',
  },
  {
    q: 'Is my code and bug data kept private?',
    a: 'Absolutely. Your bug reports, stack traces, and attachments are encrypted in transit and at rest. We do not use your data to train our models without explicit consent. Each analysis is isolated per user session and is never shared across accounts. You can request full data deletion at any time.',
  },
  {
    q: 'What file types can I attach to a bug report?',
    a: 'You can attach screenshots (JPG, PNG, WebP — up to 5MB) and log files (.log, .txt, .csv, .json — up to 2MB). Our AI reads and analyzes both image content and raw log text to provide a more complete diagnosis. Video uploads are on our roadmap.',
  },
  {
    q: 'Does SysNerve offer team and enterprise plans?',
    a: 'Yes. Our Team plan ($79/month) supports unlimited team members, GitHub, Jira, Linear, Slack, and Microsoft Teams integrations. Our Enterprise plan ($199/month) adds live server monitoring, deployment rollback suggestions, incident post-mortem reports, and a dedicated support engineer.',
  },
  {
    q: 'How does the AI actually analyze bugs?',
    a: 'SysNerve uses a multi-stage pipeline: it first classifies the bug report type (code error, user error, infrastructure issue), then extracts key signals from your stack trace and attachments. It cross-references known error patterns, runs contextual reasoning on your specific code paths, and generates a structured report with root cause, severity, confidence score, and an action plan.',
  },
  {
    q: 'Can SysNerve detect issues that are NOT actually bugs?',
    a: 'Yes — this is one of SysNerve\'s most valued features. Around 73% of "bugs" reported to engineering teams are actually user errors, misconfiguration, or expected behavior. SysNerve detects these immediately, saving your developers hours of unnecessary investigation. The verdict will clearly say "NOT A BUG" with a detailed explanation.',
  },
  {
    q: 'How fast does SysNerve return a diagnosis?',
    a: 'Most analyses complete in under 30 seconds. For reports with large log files or complex stack traces, it may take up to 60 seconds. Real-time progress indicators keep you informed throughout. On the Enterprise plan, priority processing guarantees results in under 15 seconds.',
  },
  {
    q: 'What integrations does SysNerve support?',
    a: 'SysNerve integrates with GitHub (auto-link issues to commits), Jira and Linear (create tickets from analyses), Slack and Microsoft Teams (post analysis results to channels), and Vercel (deployment-aware context). Webhook support for custom integrations is available on the Enterprise plan.',
  },
  {
    q: 'Is there a free trial available?',
    a: 'Yes. You can create a free account and run analyses immediately — no credit card required. Free accounts include up to 10 analyses per month with core features. Early waitlist members receive 30 days free on any paid plan, including Team and Enterprise. Join the waitlist to lock in this offer.',
  },
]

function AccordionItem({ item, index }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      style={{
        background: '#05080f',
        border: `1px solid ${open ? 'rgba(74,158,255,0.35)' : '#0f1e35'}`,
        borderRadius: 14,
        marginBottom: 12,
        overflow: 'hidden',
        transition: 'border-color 0.3s',
        boxShadow: open ? '0 0 24px rgba(74,158,255,0.06)' : 'none',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            minWidth: 28, height: 28, borderRadius: 8,
            background: open ? 'linear-gradient(135deg,#1d4ed8,#4a9eff)' : '#0a1120',
            border: `1px solid ${open ? '#4a9eff' : '#1e3a5f'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, color: open ? '#fff' : '#334155',
            transition: 'all 0.3s', flexShrink: 0,
          }}>
            {String(index + 1).padStart(2, '0')}
          </div>
          <span style={{
            fontSize: 15, fontWeight: 700,
            color: open ? '#e2e8f0' : '#94a3b8',
            fontFamily: "'Syne', sans-serif",
            transition: 'color 0.3s',
            lineHeight: 1.4,
          }}>
            {item.q}
          </span>
        </div>
        <div style={{
          minWidth: 24, height: 24, borderRadius: '50%',
          background: open ? 'rgba(74,158,255,0.15)' : '#0a1120',
          border: `1px solid ${open ? '#4a9eff44' : '#1e3a5f'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#4a9eff' : '#334155',
          fontSize: 12, fontWeight: 900,
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
          flexShrink: 0,
        }}>
          +
        </div>
      </button>
      <div style={{
        maxHeight: open ? 300 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{
          padding: '0 24px 22px 66px',
          fontSize: 14,
          color: '#64748b',
          lineHeight: 1.85,
          borderTop: '1px solid #0f1e35',
          paddingTop: 18,
        }}>
          {item.a}
        </div>
      </div>
    </div>
  )
}

export default function FAQ() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000',
      color: '#e2e8f0',
      fontFamily: "'Segoe UI', sans-serif",
      position: 'relative',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #1e2d45; border-radius: 3px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        h1, h2, h3 { font-family: 'Syne', sans-serif !important; }
        .back-link { color: #475569; text-decoration: none; font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid #1e3a5f; border-radius: 8px; transition: all 0.2s; background: #05080f; }
        .back-link:hover { color: #4a9eff; border-color: rgba(74,158,255,0.4); background: rgba(74,158,255,0.05); }
      `}</style>

      {/* Grid background */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'linear-gradient(#1e3a5f05 1px,transparent 1px),linear-gradient(90deg,#1e3a5f05 1px,transparent 1px)',
        backgroundSize: '56px 56px',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 820, margin: '0 auto', padding: '60px 5% 100px' }}>

        {/* Back link */}
        <div style={{ marginBottom: 48, animation: 'fadeUp 0.5s ease' }}>
          <Link to="/" className="back-link">← Back to SysNerve</Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60, animation: 'fadeUp 0.6s ease 0.1s both' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#050810', border: '1px solid #1e3a5f',
            borderRadius: 24, padding: '6px 16px', marginBottom: 24,
          }}>
            <span style={{ fontSize: 20, animation: 'float 3s ease-in-out infinite' }}>⚡</span>
            <span style={{
              fontSize: 18, fontWeight: 900,
              background: 'linear-gradient(135deg,#4a9eff,#7c3aed)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontFamily: "'Syne', sans-serif",
            }}>SysNerve</span>
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 900, margin: '0 0 14px', lineHeight: 1.1 }}>
            Frequently Asked <span style={{ color: '#4a9eff' }}>Questions</span>
          </h1>
          <p style={{ color: '#475569', fontSize: 15, margin: 0, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7 }}>
            Everything you need to know about SysNerve's AI bug intelligence platform.
          </p>
        </div>

        {/* FAQ Items */}
        <div style={{ animation: 'fadeUp 0.7s ease 0.2s both' }}>
          {faqs.map((item, i) => (
            <AccordionItem key={i} item={item} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div style={{
          marginTop: 56, textAlign: 'center',
          background: '#05080f', border: '1px solid #0f1e35',
          borderRadius: 20, padding: '40px 32px',
          animation: 'fadeUp 0.8s ease 0.3s both',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🚀</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 10px' }}>Still have questions?</h2>
          <p style={{ color: '#475569', fontSize: 14, margin: '0 0 24px', lineHeight: 1.7 }}>
            Our team is happy to walk you through SysNerve's capabilities and help you find the right plan.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" style={{
              background: 'linear-gradient(135deg,#1d4ed8,#4a9eff)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontSize: 14, fontWeight: 700, padding: '12px 24px',
              textDecoration: 'none', display: 'inline-block',
            }}>
              ⚡ Try SysNerve Free
            </Link>
            <a href="mailto:support@sysnerve.com" style={{
              background: 'transparent', border: '1px solid #1e3a5f',
              borderRadius: 10, color: '#64748b',
              fontSize: 14, fontWeight: 600, padding: '12px 24px',
              textDecoration: 'none', display: 'inline-block',
            }}>
              Contact Support
            </a>
          <footer style={{ padding: '24px 5%', borderTop: '1px solid #0a1525', textAlign: 'center', marginTop: 40 }}>
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
    <span>⚡</span>
    <span style={{ fontSize: 16, fontWeight: 900, background: 'linear-gradient(135deg,#4a9eff,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Syne',sans-serif" }}>SysNerve</span>
  </div>
  <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 8 }}>
    <Link to="/" style={{ color: '#475569', textDecoration: 'none', fontSize: 12 }}>Home</Link>
    <span style={{ color: '#1e293b' }}>·</span>
    <Link to="/faq" style={{ color: '#475569', textDecoration: 'none', fontSize: 12 }}>FAQ</Link>
    <span style={{ color: '#1e293b' }}>·</span>
    <Link to="/privacy" style={{ color: '#475569', textDecoration: 'none', fontSize: 12 }}>Privacy Policy</Link>
    <span style={{ color: '#1e293b' }}>·</span>
    <Link to="/terms" style={{ color: '#475569', textDecoration: 'none', fontSize: 12 }}>Terms of Service</Link>
  </div>
  <p style={{ color: '#1e293b', fontSize: 12, margin: 0 }}>© 2026 SysNerve — AI Bug Intelligence Platform</p>
</footer>
          </div>
        </div>
      </div>
    </div>
  )
}
 
